import useEnv from '../composables/useEnv'
import usePlan from '../composables/usePlan'
import useRestNode from '../composables/useRestNode'
import useSort from '../composables/useSort'
import usePhotoService from './usePhotoService'
import useLogger from '../composables/useLoggerLite'

/**
 * This service function provides application level services ( server side ) for executing a practitioner search.
 */
export default () => {

    const { practitionerServiceEndpoint } = useEnv()
    const { withTimings } = useLogger()

    /**
     * Executes a practitioner search.
     * The function takes as a parameter an object ( using the options pattern ) that contains the following parameters:
     * 1)  profile - The findcare profile
     * 2)  context - The context of the application as managed in the findcare applications
     * 3)  careConcepts - The careConcepts to apply to the search
     * 4)  betterChoiceContext - The betterChoiceContext to apply toward the search.
     * 5)  recordCount - The number of practitioners the service should return
     * 6)  decorators - An array of functions that can be used to apply additional attribute to each search result.  The decorator functions
     *     are supplied by the higher order function which allows the higher order function to add crosscutting behavior to location and practitioner
     *     searches while also having the benefit of executing them in the lower order search and thus gaining the benefit of being part of the 
     *     Promise.all and not having to be executed after the fact. 
     */
    const searchPractitioners = async ({searchId, profile, context, careConcepts, betterChoiceContext, recordCount = 300, decorators = [] }) => {
        
        if ( !profile ) throw new Error("Profile must be pass to the searchPractitioners service.")
        if ( !context ) throw new Error("Context must be passed to the searchPractitioners service.")
    
        const {
        type,
        latLong,
        name,
        distance,
        asOfDate,
        gender,
        acceptingNewPatients,
        tier,
        term,
        languageId,
        careNetworkId,
        hLabel,
        races,
        ethnicities,
        origins,
        } = context

        const { get } = useRestNode()
        const { getNetworkId, getGroupNumber } = usePlan(profile, context)
        const { getCpnSortString } = useSort(profile, context)

        try {
            const urlString = type === 'doctor' ? '/search-medical-practitioners' : '/search-dental-practitioners'
            const url = new URL(`${practitionerServiceEndpoint}${urlString}/network/${getNetworkId()}`)
            url.searchParams.append('recordCount', recordCount)
            if (context.sort) url.searchParams.append('sortBy', getCpnSortString(context.sort))

            if (careNetworkId && type === 'doctor') url.searchParams.append('careNetworkId', careNetworkId)
        
            if (betterChoiceContext) url.searchParams.append('betterChoiceContext', betterChoiceContext)
            if (latLong) {
                url.searchParams.append('latitude', latLong.split(',')[0])
                url.searchParams.append('longitude', latLong.split(',')[1])
            }
            if (getGroupNumber()) url.searchParams.append('employerGroupId', getGroupNumber())
            if (name) url.searchParams.append('lastName', name)
            if (distance) url.searchParams.append('distance', distance)
            if (asOfDate) url.searchParams.append('asof', asOfDate)
            if (languageId) url.searchParams.append('language', languageId)
            if (gender) url.searchParams.append('gender', gender)
            if (acceptingNewPatients) url.searchParams.append('acceptingNewPatients', acceptingNewPatients)
            if (hLabel) url.searchParams.append('hospitalAffiliation', hLabel)
        
            if (tier) url.searchParams.append('tier', tier)
        
            if (term) {    
                //If there are no concepts available for the term return no results.
                if (careConcepts && careConcepts.length > 0) {
                    url.searchParams.append('careConcepts', careConcepts.join())
                } else return []
            }
        
            // race & ethnicity
            if (races && races.length > 0) {
                url.searchParams.append('races', races.join(','))
            }
            if (ethnicities && ethnicities.length > 0) {
                url.searchParams.append('ethnicities', ethnicities.join(','))
            }
        
            // country of origin
            if (origins && origins.length > 0) {
                url.searchParams.append('origins', origins.join(','))
            }
            //console.log(url.toString())
        
            //const result = await get(url.toString())
            const result = await withTimings(`searchPractitioners (${searchId})`,get, url.toString())
        
            const retVal = result.value.practitioners

            //Decorate the practitioners with additional params
            await decorate(searchId, retVal, decorators)
            return retVal

        } catch (error) {
            console.log(error)
        return []
        }
    }

    /**
     * This function decorates the results with extra stuff.  
     * 1)  Practitioner photos
     * 2)  Cycles through each decorator function in the decorators collection and applies the function on each result.
     */
    const decorate = async (searchId, results, decorators) => {
        const { practitionerPhotos } = usePhotoService()
        try {
            //const photos = await practitionerPhotos(results)
            const photos = await withTimings(`practitionerPhotos (${searchId})`, practitionerPhotos, results)
            if (photos) {
                results.forEach((item) => {
                    decorators.forEach(f => f(item))
                    const photo = photos[item.practitionerId]
                    if (photo) item.photoUrl = photo.url
                })
            }   
        }
        catch(error) {
            console.log(error)
        } 
    }

    return { searchPractitioners, decorate }

}